/**
 * ReuseLibraryContext.tsx
 *
 * Global context for tracking background reuse library indexing jobs.
 * Persists across page/route navigation so the user can freely switch
 * to Chat, Settings, etc. while the index build runs in the background.
 *
 * Usage:
 *   - Wrap the app with <ReuseLibraryProvider>
 *   - Call `startJob(clientId)` from any component to begin a job
 *   - Read `job` for the latest status; `isActive` is true while running
 */
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircleIcon, CloseIcon, ErrorIcon } from '../components/feather-icons';
import { API_BASE_URL } from '../config';
import '../components/rag-chat-shell.css';

// ===========================
// Types
// ===========================

export type ReuseLibraryJobStatus =
    | 'idle'
    | 'queued'
    | 'fetching'
    | 'storing'
    | 'completed'
    | 'failed';

export interface ReuseLibraryJobState {
    jobId: string | null;
    status: ReuseLibraryJobStatus;
    error?: string;
    result?: {
        totalEntries: number;
        totalNodes?: number;
        succeededNodes?: string[];
        failedNodes?: Array<{ node: string; error: string }>;
    };
}

interface ReuseLibraryContextType {
    job: ReuseLibraryJobState;
    isActive: boolean;
    /** Start a new background indexing job. Returns the jobId or null on failure. */
    startJob: (clientId: string, selectedNodes?: string[]) => Promise<string | null>;
    /** Dismiss the completed/failed job notification */
    dismissJob: () => void;
}

// ===========================
// Defaults
// ===========================

const INITIAL_JOB: ReuseLibraryJobState = {
    jobId: null,
    status: 'idle',
};

const POLL_INTERVAL_MS = 3000; // Poll every 3 seconds
const ACTIVE_STATUSES: ReuseLibraryJobStatus[] = ['queued', 'fetching', 'storing'];

// ===========================
// Context
// ===========================

const ReuseLibraryContext = createContext<ReuseLibraryContextType>({
    job: INITIAL_JOB,
    isActive: false,
    startJob: async () => null,
    dismissJob: () => {},
});

export const useReuseLibrary = () => useContext(ReuseLibraryContext);

// ===========================
// Provider
// ===========================

export const ReuseLibraryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { t } = useTranslation();
    const [job, setJob] = useState<ReuseLibraryJobState>(INITIAL_JOB);
    const [toastOpen, setToastOpen] = useState(false);
    const [toastSeverity, setToastSeverity] = useState<'success' | 'error'>('success');
    const [toastMessage, setToastMessage] = useState('');

    const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const stopPolling = useCallback(() => {
        if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    }, []);

    /** Fetch job status from server and update local state */
    const pollJobStatus = useCallback(async (jobId: string) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/v1/rag/reuse-library-index/${jobId}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    Accept: 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();
            if (!data.success) throw new Error(data.error || 'Unknown error');

            const serverJob = data.data;
            setJob(prev => ({
                ...prev,
                status: serverJob.status as ReuseLibraryJobStatus,
                error: serverJob.error,
                result: serverJob.result,
            }));

            // Check if finished
            if (serverJob.status === 'completed') {
                stopPolling();
                const failedNodes: Array<{ node: string; error: string }> = serverJob.result?.failedNodes ?? [];
                if (failedNodes.length > 0) {
                    // Partial success: some nodes were skipped due to errors.
                    const failedList = failedNodes.map(f => f.node).join(', ');
                    setToastMessage(
                        t(
                            'reuseLibrary.bgPartialSuccess',
                            'Reuse library index built: {{count}} entries stored. {{failedCount}} node(s) skipped due to errors: {{failedList}}',
                            {
                                count: serverJob.result?.totalEntries ?? 0,
                                failedCount: failedNodes.length,
                                failedList,
                            }
                        )
                    );
                    // Show as warning-style via 'error' severity is harsh; keep 'success' but
                    // the message itself makes the partial failure visible.
                    setToastSeverity('success');
                } else {
                    setToastMessage(
                        t('reuseLibrary.bgSuccess', 'Reuse library index built: {{count}} entries stored.', {
                            count: serverJob.result?.totalEntries ?? 0,
                        })
                    );
                    setToastSeverity('success');
                }
                setToastOpen(true);
            } else if (serverJob.status === 'failed') {
                stopPolling();
                setToastMessage(
                    t('reuseLibrary.bgFailed', 'Reuse library index build failed: {{error}}', {
                        error: serverJob.error || 'Unknown error',
                    })
                );
                setToastSeverity('error');
                setToastOpen(true);
            }
        } catch (err: any) {
            console.error('[ReuseLibraryContext] Polling error:', err.message);
            // Don't stop polling on transient network errors
        }
    }, [stopPolling, t]);

    /** Start polling for an active job */
    const startPolling = useCallback((jobId: string) => {
        stopPolling();
        // Immediate first poll
        pollJobStatus(jobId);
        pollTimerRef.current = setInterval(() => pollJobStatus(jobId), POLL_INTERVAL_MS);
    }, [stopPolling, pollJobStatus]);

    /** Start a new background indexing job */
    const startJob = useCallback(async (clientId: string, selectedNodes?: string[]): Promise<string | null> => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/v1/rag/reuse-library-index`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify({ clientId, selectedNodes }),
            });

            // Guard against proxy/Cloudflare returning HTML on timeout
            const contentType = response.headers.get('content-type') || '';
            if (!contentType.includes('application/json')) {
                const text = await response.text();
                console.error('[ReuseLibraryContext] Non-JSON response:', response.status, text.substring(0, 200));
                throw new Error(
                    response.status === 524 || response.status === 504 || response.status === 502
                        ? t('reuseLibrary.proxyTimeout', 'Request timed out (HTTP {{status}}). Please check proxy/Cloudflare timeout settings.', { status: response.status })
                        : t('reuseLibrary.unexpectedResponse', 'Unexpected response from server (HTTP {{status}}). Please check that the backend server is running.', { status: response.status })
                );
            }

            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Failed to start job');
            }

            const newJobId: string = data.jobId;
            setJob({
                jobId: newJobId,
                status: 'queued',
            });
            startPolling(newJobId);
            return newJobId;
        } catch (err: any) {
            console.error('[ReuseLibraryContext] Failed to start job:', err.message);
            setJob(prev => ({
                ...prev,
                status: 'failed',
                error: err.message,
            }));
            setToastMessage(err.message || t('reuseLibrary.startFailed', 'Failed to start reuse library index job'));
            setToastSeverity('error');
            setToastOpen(true);
            return null;
        }
    }, [startPolling, t]);

    /** Dismiss completed/failed notification */
    const dismissJob = useCallback(() => {
        stopPolling();
        setJob(INITIAL_JOB);
    }, [stopPolling]);

    // Cleanup on unmount
    useEffect(() => {
        return () => stopPolling();
    }, [stopPolling]);

    const isActive = ACTIVE_STATUSES.includes(job.status);

    return (
        <ReuseLibraryContext.Provider value={{ job, isActive, startJob, dismissJob }}>
            {children}

            {/* Toast notification on completion / failure */}
            {toastOpen && (
                <div className="industrial-toast-stack">
                    <div className={`industrial-toast industrial-toast--${toastSeverity}`} role="status">
                        {toastSeverity === 'success' ? <CheckCircleIcon fontSize="small" /> : <ErrorIcon fontSize="small" />}
                        <span>{toastMessage}</span>
                        <button type="button" onClick={() => setToastOpen(false)} aria-label={t('close', 'Close')}>
                            <CloseIcon fontSize="small" />
                        </button>
                    </div>
                </div>
            )}
        </ReuseLibraryContext.Provider>
    );
};

export default ReuseLibraryContext;
